import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Spin, Typography, Empty, Statistic, Space, Tag } from 'antd';
import { 
  ClockCircleOutlined,
  Loading3QuartersOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DollarOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  RightOutlined
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import './AdminFacilityStatusSummary.css';

const { Text, Title } = Typography;

// Status configuration matching ClaimsAdminNewHook
const STATUS_CONFIG = {
  0: { 
    text: 'Pending CO', 
    color: '#121221', 
    icon: <ClockCircleOutlined />,
    bgColor: '#f0f0f0',
    description: 'Claims pending CO review'
  },
  1: { 
    text: 'Pending HM', 
    color: '#ff9200', 
    icon: <Loading3QuartersOutlined />,
    bgColor: '#fff7e6',
    description: 'Claims pending HM approval'
  },
  2: { 
    text: 'Pending CFO', 
    color: '#afaa0b', 
    icon: <ExclamationCircleOutlined />,
    bgColor: '#fffbe6',
    description: 'Claims pending CFO approval'
  },
  3: { 
    text: 'Paid', 
    color: '#00aa00', 
    icon: <CheckCircleOutlined />,
    bgColor: '#f6ffed',
    description: 'Claims that have been paid'
  },
  4: { 
    text: 'Denied', 
    color: '#f5222d', 
    icon: <CloseCircleOutlined />,
    bgColor: '#fff1f0',
    description: 'Claims that have been denied'
  },
};

/**
 * AdminFacilityStatusSummary - Phase 1: Displays status summary cards for selected facility
 * Shows status groups with counts (fast, no claim details)
 * Claims are loaded on-demand when status card is expanded (Phase 2)
 */
const AdminFacilityStatusSummary = ({ month, facilityId, onStatusClick, onClaimClick }) => {
  const [statusGroups, setStatusGroups] = useState([]);
  const [facility, setFacility] = useState(null);
  const [totals, setTotals] = useState({
    total_claims: 0,
    total_cost: 0,
    submitted_total: 0,
    pa_approved_total: 0,
    claims_approved_total: 0,
  });
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  // Fetch status summary (Phase 1: Fast)
  const fetchStatusSummary = async () => {
    if (!month || !facilityId) return;

    // Get stable keys
    const monthKey = typeof month === 'string' ? month : (month.month || month.key || month);
    const facilityIdNum = typeof facilityId === 'object' ? (facilityId.facility_id || facilityId.id) : facilityId;

    // Prevent duplicate calls
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    setLoading(true);
    try {
      const response = await lib.getAdminFacilityStatusSummary(monthKey, facilityIdNum);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch status summary',
        });
        return;
      }

      // Backend returns: { error, message, data: { month, month_label, facility: {...}, status_groups: [...], totals: {...} } }
      const responseData = response?.data || {};
      const data = responseData.data || {};
      
      setStatusGroups(data.status_groups || []);
      setFacility(data.facility || null);
      setTotals(data.totals || { 
        total_claims: 0, 
        total_cost: 0,
        submitted_total: 0,
        pa_approved_total: 0,
        claims_approved_total: 0,
        unique_enrollees: 0,
        average_claim_cost: 0,
        claims_with_queries: 0,
        total_items_count: 0
      });
    } catch (error) {
      console.error('Error fetching status summary:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to fetch status summary',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Fetch status summary when month or facilityId changes
  useEffect(() => {
    fetchStatusSummary();
  }, [month, facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle status card click (navigate to claims list)
  const handleStatusCardClick = (statusCode) => {
    if (onStatusClick) {
      onStatusClick(statusCode);
    }
  };

  // Get status config for a status code
  const getStatusConfig = (statusCode) => {
    return STATUS_CONFIG[statusCode] || {
      text: `Status ${statusCode}`,
      color: '#666',
      icon: <ClockCircleOutlined />,
      bgColor: '#f5f5f5',
      description: 'Unknown status'
    };
  };

  if (loading && statusGroups.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!loading && statusGroups.length === 0) {
    return (
      <Empty
        description="No claims found for this facility"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div className="admin-facility-status-summary">
      {/* Facility Header */}
      {facility && (
        <div style={{ marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0 }}>
            {facility.facility_name || 'Facility'}
          </Title>
          <Text type="secondary">
            {facility.facility_city}, {facility.facility_state}
          </Text>
        </div>
      )}

      {/* Totals Summary - Compact Colorful Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
              border: '2px solid #1890ff',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(24, 144, 255, 0.15)',
              overflow: 'hidden',
              position: 'relative',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(24, 144, 255, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '11px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '6px' }}>Total Claims</Text>
                <Text style={{ fontSize: '22px', fontWeight: 600, color: '#0050b3', display: 'block', marginBottom: '4px' }}>{totals.total_claims || 0}</Text>
                <div style={{ height: '1px', background: 'rgba(24, 144, 255, 0.3)', marginTop: '6px' }} />
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(24, 144, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#1890ff', flexShrink: 0 }}>
                <FileTextOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
              border: '2px solid #fa8c16',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(250, 140, 22, 0.15)',
              overflow: 'hidden',
              position: 'relative',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(250, 140, 22, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '11px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '6px' }}>Claims approved</Text>
                <Text style={{ fontSize: '20px', fontWeight: 600, color: '#d46b08', display: 'block', marginBottom: '4px' }}>
                  <CurrencyFormat
                    value={getClaimsApprovedTotal(totals)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'₦'}
                    decimalScale={2}
                  />
                </Text>
                <div style={{ height: '1px', background: 'rgba(250, 140, 22, 0.3)', marginTop: '6px' }} />
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(250, 140, 22, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#fa8c16', flexShrink: 0 }}>
                <DollarOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
              border: '2px solid #2f54eb',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(47, 84, 235, 0.15)',
              overflow: 'hidden',
              position: 'relative',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(47, 84, 235, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '11px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '6px' }}>Submitted to PA</Text>
                <Text style={{ fontSize: '18px', fontWeight: 600, color: '#1d39c4', display: 'block', marginBottom: '4px' }}>
                  <CurrencyFormat
                    value={getSubmittedTotal(totals)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'₦'}
                    decimalScale={2}
                  />
                </Text>
                <div style={{ height: '1px', background: 'rgba(47, 84, 235, 0.3)', marginTop: '6px' }} />
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(47, 84, 235, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#2f54eb', flexShrink: 0 }}>
                <DollarOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
              border: '2px solid #722ed1',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
              overflow: 'hidden',
              position: 'relative',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '11px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '6px' }}>PA approved</Text>
                <Text style={{ fontSize: '18px', fontWeight: 600, color: '#531dab', display: 'block', marginBottom: '4px' }}>
                  <CurrencyFormat
                    value={getPaApprovedTotal(totals)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'₦'}
                    decimalScale={2}
                  />
                </Text>
                <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginTop: '6px' }} />
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(114, 46, 209, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#722ed1', flexShrink: 0 }}>
                <DollarOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
              border: '2px solid #1890ff',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(24, 144, 255, 0.15)',
              overflow: 'hidden',
              position: 'relative',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(24, 144, 255, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '11px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '6px' }}>Unique Enrollees</Text>
                <Text style={{ fontSize: '22px', fontWeight: 600, color: '#0050b3', display: 'block', marginBottom: '4px' }}>{totals.unique_enrollees || 0}</Text>
                <div style={{ height: '1px', background: 'rgba(24, 144, 255, 0.3)', marginTop: '6px' }} />
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(24, 144, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#1890ff', flexShrink: 0 }}>
                <UserOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card
            style={{
              height: '100%',
              background: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
              border: '2px solid #f5222d',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(245, 34, 45, 0.15)',
              overflow: 'hidden',
              position: 'relative',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(245, 34, 45, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '11px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '6px' }}>Claims with Queries</Text>
                <Text style={{ fontSize: '22px', fontWeight: 600, color: '#cf1322', display: 'block', marginBottom: '4px' }}>{totals.claims_with_queries || 0}</Text>
                <div style={{ height: '1px', background: 'rgba(245, 34, 45, 0.3)', marginTop: '6px' }} />
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(245, 34, 45, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#f5222d', flexShrink: 0 }}>
                <QuestionCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Visual Separator */}
      <div style={{ 
        marginBottom: '24px', 
        paddingTop: '24px',
        borderTop: '2px solid #e8e8e8',
        position: 'relative'
      }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px', color: '#262626' }}>
          Status Summary
        </Title>
      </div>

      {/* Status Cards - Matching Month Cards Style */}
      <Row gutter={[16, 16]}>
        {statusGroups.map((group) => {
          const statusConfig = getStatusConfig(group.status_code);

          return (
            <Col xs={24} sm={12} md={8} lg={6} xl={4} key={group.status_code}>
              <Card
                className="status-card"
                hoverable
                onClick={() => handleStatusCardClick(group.status_code)}
                style={{
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid #f0f0f0',
                  boxShadow: `0 2px 8px ${statusConfig.color}20`,
                }}
              >
                <div className="status-card__content">
                  <div className="status-card__header">
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: '8px',
                      color: statusConfig.color,
                      fontSize: '20px'
                    }}>
                      {statusConfig.icon}
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                      {group.status_label || statusConfig.text}
                    </Title>
                  </div>
                  
                  <div className="status-card__stats">
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <FileTextOutlined style={{ marginRight: '4px', color: statusConfig.color }} />
                        <Tag color={group.status_code === 0 ? 'default' : group.status_code === 1 ? 'orange' : group.status_code === 2 ? 'gold' : group.status_code === 3 ? 'green' : 'red'}>
                          {group.claims_count || 0}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {group.claims_count === 1 ? 'claim' : 'claims'}
                        </Text>
                      </div>
                      <div>
                        <UserOutlined style={{ marginRight: '4px', color: statusConfig.color }} />
                        <Tag color="blue">{group.unique_enrollees || 0}</Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {group.unique_enrollees === 1 ? 'enrollee' : 'enrollees'}
                        </Text>
                      </div>
                      <div>
                        <DollarOutlined style={{ marginRight: '4px', color: statusConfig.color }} />
                        <Space direction="vertical" size={0} style={{ display: 'inline-flex', verticalAlign: 'top' }}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Submitted{' '}
                            <CurrencyFormat
                              value={getSubmittedTotal(group)}
                              displayType={'text'}
                              thousandSeparator={true}
                              prefix={'₦'}
                              decimalScale={0}
                            />
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            PA approved{' '}
                            <CurrencyFormat
                              value={getPaApprovedTotal(group)}
                              displayType={'text'}
                              thousandSeparator={true}
                              prefix={'₦'}
                              decimalScale={0}
                            />
                          </Text>
                          <Text style={{ fontSize: '12px', fontWeight: 600 }}>
                            Claims approved{' '}
                            <CurrencyFormat
                              value={getClaimsApprovedTotal(group)}
                              displayType={'text'}
                              thousandSeparator={true}
                              prefix={'₦'}
                              decimalScale={0}
                            />
                          </Text>
                        </Space>
                      </div>
                    </Space>
                  </div>

                  <div className="status-card__action" style={{ color: statusConfig.color }}>
                    <RightOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default AdminFacilityStatusSummary;

